// ** React Imports
import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

// ** Third Party Components
import { Field, Formik, Form } from "formik";
import * as yup from "yup";
import { useWeb3React } from "@web3-react/core";

// ** Reactstrap Imports
import toast from "react-hot-toast";
import {
	Row,
	Col,
	Card,
	Input,
	Label,
	Button,
	CardBody,
	CardTitle,
	CardHeader,
	FormFeedback,
} from "reactstrap";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useHistory } from "react-router-dom";
import { saveUser, uploadAvatar } from "../store/actions/user";
import { chainIds } from "../constants";

export default function Profile() {
	// ** Hooks
	const { chainId } = useWeb3React();
	const { account, user } = useSelector((state) => state.user);
	const history = useHistory();
	const dispatch = useDispatch();
	const [network, setNetwork] = useState(null);

	const defaultValues = {
		name: "",
		customURL: "https://www.certifylabs.com",
		bio: "",
		twitter: "@",
		personalSite: "",
		email: "",
		userPinataAPIKey: "",
		userPinataAPISecret: "",
		userPinataJWT: "",
		userPinataDomen: "",
	};

	useEffect(() => {
		setNetwork(chainId);
	}, [chainId]);

	const getAvatar = () => {
		if (user.image) {
			return `${process.env.REACT_APP_API_DO_URL}/${user.image}`;
		} else return "../assets/img/user-Image17.png";
	};
	const getItemForForm = () => {
		const initialValues = user?.name ? user : defaultValues;
		return initialValues;
	};

	const userSchema = yup.object().shape({
		_id: yup.string(),
		account: yup.string(),
		name: yup.string().min(3, "Very short name").required("Name is required"),
		customURL: yup.string().url("Enter correct URL"),
		bio: yup.string(),
		twitter: yup
			.string()
			.test("", "Enter right Twitter User name!", (value) => /^@/.test(value)),
		personalSite: yup.string().url(),
		email: yup.string().email().required("E-mail is required"),
		userPinataDomen: yup
			.string()
			.url()
			.test(
				"",
				"Enter right URL (trailing slash is required)",
				(value) => !value || (value && /\/$/.test(value))
			),
	});

	const onChangeAvatar = (e) => {
		let file = e.target.files[0];
		if (file) {
			if (file?.size > 0 && file.size <= 800 * 1024) {
				dispatch(uploadAvatar(user._id, file));
			} else {
				toast.error(
					`File size ${Math.round(file.size / 1024)}kB, no more 800kB`
				);
			}
		}
	};

	const onFormSubmit = (data) => {
		data._id = user._id;
		data.account = account;
		delete data.image;
		delete data.isAdmin;
		delete data.batchMint;
		delete data.isOwnPinata;
		dispatch(saveUser(data));
		history.goBack();
	};

	return (
		<Container maxWidth="md">
			<Card>
				<CardHeader className="border-bottom">
					<CardTitle tag="h4">Profile - Edit profile</CardTitle>
				</CardHeader>
				<CardBody className="py-2 my-25">
					<div className="d-flex">
						<div className="me-25">
							<img
								className="rounded me-50"
								src={getAvatar()}
								alt="user avatar"
								height="100"
								width="100"
							/>
						</div>
						<div className="d-flex align-items-end mt-75 ms-1">
							<div>
								<Button
									tag={Label}
									className="mb-75 me-75"
									size="sm"
									color="primary"
								>
									Upload
									<Input
										type="file"
										onChange={onChangeAvatar}
										hidden
										accept="image/jpg, image/jpeg, image/gif, image/png"
									/>
								</Button>
								<p className="mb-0">
									Allowed JPG, GIF or PNG. Max size of 800kB
								</p>
							</div>
						</div>
						<div>Connected {chainIds[network]?.name || ""}</div>
					</div>
					<Formik
						initialValues={getItemForForm()}
						enableReinitialize={true}
						validateOnBlur
						validationSchema={userSchema}
						onSubmit={onFormSubmit}
					>
						<Form className="mt-2 pt-50">
							<Row>
								<Col sm="6" className="mb-1">
									<Label className="form-label" for="name">
										Display name
									</Label>
									<Field name="name" type="text">
										{({ field, meta }) => (
											<>
												<Input
													id="name"
													placeholder="Anonymous"
													invalid={meta.touched && meta.error && true}
													{...field}
												/>

												{meta.touched && meta.error && (
													<FormFeedback>Please enter a valid Name</FormFeedback>
												)}
											</>
										)}
									</Field>
								</Col>
								<Col sm="6" className="mb-1">
									<Label className="form-label" for="customURL">
										Custom URL
									</Label>
									<Field name="customURL" type="text">
										{({ field, meta }) => (
											<>
												<Input
													id="customURL"
													placeholder="https://www.certifylabs.com"
													invalid={meta.touched && meta.error && true}
													{...field}
												/>

												{meta.touched && meta.error && (
													<FormFeedback>Please enter a valid URL</FormFeedback>
												)}
											</>
										)}
									</Field>
								</Col>
								<Col sm="6" className="mb-1">
									<Label className="form-label" for="bio">
										Bio
									</Label>
									<Field name="bio" type="text">
										{({ field, meta }) => (
											<Input
												id="bio"
												placeholder=""
												invalid={meta.touched && meta.error && true}
												{...field}
											/>
										)}
									</Field>
								</Col>
								<Col sm="6" className="mb-1">
									<Label className="form-label" for="twitter">
										Username's Twitter
									</Label>
									<Field name="twitter" type="text">
										{({ field, meta }) => (
											<>
												<Input
													id="twitter"
													placeholder=""
													invalid={meta.touched && meta.error && true}
													{...field}
												/>

												{meta.touched && meta.error && (
													<FormFeedback>
														Please enter a valid Twitter account
													</FormFeedback>
												)}
											</>
										)}
									</Field>
								</Col>
								<Col sm="6" className="mb-1">
									<Label className="form-label" for="personalSite">
										Personal site or portfolio
									</Label>
									<Field name="personalSite" type="text">
										{({ field, meta }) => (
											<>
												<Input
													id="personalSite"
													placeholder=""
													invalid={meta.touched && meta.error && true}
													{...field}
												/>

												{meta.touched && meta.error && (
													<FormFeedback>Please enter a valid URL</FormFeedback>
												)}
											</>
										)}
									</Field>
								</Col>
								<Col sm="6" className="mb-1">
									<Label className="form-label" for="email">
										E-mail
									</Label>
									<Field name="email" type="email">
										{({ field, meta }) => (
											<>
												<Input
													id="email"
													placeholder=""
													invalid={meta.touched && meta.error && true}
													{...field}
												/>

												{meta.touched && meta.error && (
													<FormFeedback>
														Please enter a valid e-mail
													</FormFeedback>
												)}
											</>
										)}
									</Field>
								</Col>

								{user.isOwnPinata && (
									<>
										<Typography
											className="form-label"
											sx={{ width: "100%", my: "1rem" }}
										>
											Your Pinata API Gateway credentials
										</Typography>
										<Col sm="6" className="mb-1">
											<Label className="form-label" for="pinataKey">
												API Key
											</Label>
											<Field name="userPinataAPIKey" type="text">
												{({ field, meta }) => (
													<>
														<Input
															id="pinataKey"
															placeholder=""
															invalid={meta.touched && meta.error && true}
															{...field}
														/>
													</>
												)}
											</Field>
										</Col>
										<Col sm="6" className="mb-1">
											<Label className="form-label" for="pinataSecret">
												API Secret
											</Label>
											<Field name="userPinataAPISecret" type="text">
												{({ field, meta }) => (
													<>
														<Input
															id="pinataSecret"
															placeholder=""
															invalid={meta.touched && meta.error && true}
															{...field}
														/>
													</>
												)}
											</Field>
										</Col>
										<Col sm="12" className="mb-1">
											<Label className="form-label" for="pinataJWT">
												JWT
											</Label>
											<Field name="userPinataJWT">
												{({ field, meta }) => (
													<>
														<Input id="pinataJWT" type="textarea" {...field} />
													</>
												)}
											</Field>
										</Col>
										<Col sm="12" className="mb-1">
											<Label className="form-label" for="pinataDomen">
												Gateway custom domain name
											</Label>
											<Field name="userPinataDomen" type="text">
												{({ field, meta }) => (
													<>
														<Input
															id="pinataDomen"
															placeholder="https://"
															invalid={meta.touched && meta.error && true}
															{...field}
														/>
														{meta.touched && meta.error && (
															<FormFeedback>{meta.error}</FormFeedback>
														)}
													</>
												)}
											</Field>
										</Col>
									</>
								)}

								<Col className="mt-2" sm="12">
									<Button type="submit" className="me-1" color="primary">
										Save changes
									</Button>
								</Col>
							</Row>
						</Form>
					</Formik>
				</CardBody>
			</Card>
		</Container>
	);
}
